/**
 * boardModel — PURE: the R7 change graph → what the signal board draws (leg `gui-signal-board`).
 *
 * No React, no I/O. Everything the board needs to decide lives here so it is unit-tested without a DOM:
 *
 *  - TRACKS are build levels, left to right (the lowest level is the leftmost, the first to be built).
 *    ORDER comes from `depends_on` edges ONLY. `derived_from` is provenance and points the other way
 *    (R7's contract: using it for order shows 2-cycles); `reference` is not ordering. Before a build
 *    exists the nodes carry no `level` (Work's rows supply it), so depth is derived on the client from
 *    `depends_on` — cycle-safe, never throws.
 *  - A STATION is the nodes with the same level AND metadata type. Nothing in a plan carries a cluster
 *    today and this rule is generic (rule 18): no per-org or per-type enumeration.
 *  - LAMPS: one meaning each (`LAMP_LABEL`). `built` (generated, not yet checked) and `validated` are
 *    DIFFERENT lamps (rule 23: never show unchecked work as checked). The validation LABEL
 *    (`not_validated` / `validation_failed`) is a RING, separate from the build-status lamp.
 *  - The ACTIVE track is the lowest one that still has unsettled nodes, only once a build exists.
 *  - LAYOUT is arithmetic on ids and counts (`layoutBoard`): deterministic ordering, no DOM measuring,
 *    so a refetch never re-lays anything out unless the data changed.
 */
import type { GraphEdge, GraphNode, TaskGraph } from "@/services/multi-agent/protocol";

// ── lamps ────────────────────────────────────────────────────────────────────────────────────────

/** `unlit` = no build row yet; `unknown` = a status this client does not know (server newer than client). */
export type LampKind = "unlit" | "dim" | "pulsing" | "built" | "validated" | "failed" | "blocked" | "dropped" | "unknown";
export type RingKind = "not_validated" | "validation_failed";

/** Fixed display order of a station's lamps (also the legend order). */
export const LAMP_ORDER: readonly LampKind[] = ["failed", "blocked", "pulsing", "dim", "unlit", "built", "validated", "dropped", "unknown"];
export const RING_ORDER: readonly RingKind[] = ["validation_failed", "not_validated"];

export const LAMP_LABEL: Record<LampKind, string> = {
  unlit: "Planned, not built yet",
  dim: "Waiting to be built",
  pulsing: "Being built now",
  built: "Built, check pending",
  validated: "Built and checked",
  failed: "Failed",
  blocked: "Blocked behind another item",
  dropped: "Dropped",
  unknown: "Unrecognised status",
};
export const RING_LABEL: Record<RingKind, string> = {
  not_validated: "Not checked against the org",
  validation_failed: "Org check failed",
};

export function lampOf(node: Pick<GraphNode, "build_status"> & Partial<Pick<GraphNode, "validation_status">>): LampKind {
  // Rule 23: never solid-green work whose org check did not pass; it stays the "built" outline with its ring.
  if (node.build_status === "validated" && ringOf({ validation_status: node.validation_status }) !== null) return "built";
  switch (node.build_status ?? null) {
    case null:
      return "unlit";
    case "pending":
      return "dim";
    case "leased":
      return "pulsing";
    case "built":
      return "built";
    case "validated":
      return "validated";
    case "failed":
      return "failed";
    case "blocked":
      return "blocked";
    case "dropped":
      return "dropped";
    default:
      return "unknown";
  }
}

export function ringOf(node: Pick<GraphNode, "validation_status">): RingKind | null {
  return node.validation_status === "not_validated" || node.validation_status === "validation_failed" ? node.validation_status : null;
}

/**
 * The SAME fact at the status level (one owner, rule 21b): a work-item status the server counts as resolved
 * (`work/item_facts.py` RESOLVED_STATUSES). The task-panel pace figure (`etaModel.ts`) reads this too.
 */
export const RESOLVED_BUILD_STATUSES: ReadonlySet<string> = new Set(["built", "validated", "blocked", "failed", "dropped"]);
export function isResolvedBuildStatus(status: string | null | undefined): boolean {
  return status != null && RESOLVED_BUILD_STATUSES.has(status);
}

const KNOWN_BUILD_STATUSES = ["pending", "leased", "built", "validated", "failed", "blocked", "dropped"] as const;
/** Lamps with build work still to come, DERIVED from the resolved set above so the two cannot drift: no build row
 *  (`unlit`) and a status this client does not know (`unknown`) are unresolved by definition; the rest follow the set. */
const UNRESOLVED_LAMPS: ReadonlySet<LampKind> = new Set<LampKind>([
  "unlit",
  "unknown",
  ...KNOWN_BUILD_STATUSES.filter((st) => !RESOLVED_BUILD_STATUSES.has(st)).map((st) => lampOf({ build_status: st })),
]);

// ── model ────────────────────────────────────────────────────────────────────────────────────────

export interface LampCount {
  lamp: LampKind;
  count: number;
}
export interface RingCount {
  ring: RingKind;
  count: number;
}
export interface Station {
  /** `L<level>|<metadata_type>` — stable across refetches (keyed by ids, not by position). */
  id: string;
  /** Index into `tracks` of the track this station sits on. */
  track: number;
  metadataType: string;
  /** Node ids, sorted (full_name, then id). */
  itemIds: string[];
  count: number;
  lamps: LampCount[];
  rings: RingCount[];
  settled: boolean;
}
export interface Track {
  index: number;
  /** The build level when the graph carries one; otherwise the derived depth. */
  level: number;
  stationIds: string[];
  itemCount: number;
  settled: boolean;
  active: boolean;
}
export interface AggregatedEdge {
  /** The station of the dependency (built first, drawn on the left). */
  source: string;
  /** The station of the dependent. */
  target: string;
  count: number;
}
export interface Board {
  tracks: Track[];
  stations: Station[];
  /** Whole-graph lamp totals (legend counts). */
  lamps: LampCount[];
  aggregatedEdges: AggregatedEdge[];
  /** Index of the active track, or `null` (no build yet, or everything settled). */
  activeTrack: number | null;
  /** Node id → station id. */
  stationOf: Record<string, string>;
  /** Node id → node. */
  nodes: Record<string, GraphNode>;
  /** Deduped `depends_on` pairs as `[dependency, dependent]`, sorted — the only ordering relation. */
  orderEdges: Array<[string, string]>;
}

// ── depth ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Longest-dependency-chain depth per node, from `depends_on` only. `deps` maps a node to what it
 * depends on (known ids only, no self edge). Kahn's algorithm; nodes left over by a cycle get
 * `max(depth of their already-placed dependencies) + 1` in id order, so a cycle cannot loop or throw.
 */
export function deriveDepths(
  ids: readonly string[],
  deps: ReadonlyMap<string, ReadonlySet<string>>,
  fixed: ReadonlyMap<string, number> = new Map(),
): Map<string, number> {
  const depth = new Map<string, number>();
  const remaining = new Map<string, number>();
  const dependents = new Map<string, string[]>();
  for (const id of ids) {
    const d = deps.get(id);
    remaining.set(id, d ? d.size : 0);
    if (d) for (const dep of d) (dependents.get(dep) ?? dependents.set(dep, []).get(dep)!).push(id);
  }
  const queue = ids.filter((id) => remaining.get(id) === 0).sort();
  for (let head = 0; head < queue.length; head++) {
    const id = queue[head];
    let best = -1;
    for (const dep of deps.get(id) ?? []) best = Math.max(best, depth.get(dep) ?? -1);
    depth.set(id, fixed.get(id) ?? best + 1);
    for (const next of dependents.get(id) ?? []) {
      const left = (remaining.get(next) ?? 0) - 1;
      remaining.set(next, left);
      if (left === 0) queue.push(next);
    }
  }
  for (const id of [...ids].sort()) {
    if (depth.has(id)) continue;
    let best = -1;
    for (const dep of deps.get(id) ?? []) best = Math.max(best, depth.get(dep) ?? -1);
    depth.set(id, fixed.get(id) ?? best + 1);
  }
  return depth;
}

// ── the pure function ────────────────────────────────────────────────────────────────────────────

const byName = (a: GraphNode, b: GraphNode) =>
  a.full_name < b.full_name ? -1 : a.full_name > b.full_name ? 1 : a.item_id < b.item_id ? -1 : a.item_id > b.item_id ? 1 : 0;

function tally<K extends string>(keys: Iterable<K>, order: readonly K[]): Array<{ key: K; count: number }> {
  const counts = new Map<K, number>();
  for (const k of keys) counts.set(k, (counts.get(k) ?? 0) + 1);
  return order.filter((k) => counts.has(k)).map((k) => ({ key: k, count: counts.get(k)! }));
}

/** `graph → { tracks, stations, lamps, aggregatedEdges, activeTrack }` (+ the lookups the view needs). */
export function buildBoard(graph: Pick<TaskGraph, "nodes" | "edges">, opts: { buildActive?: boolean } = {}): Board {
  const nodeList = [...(graph.nodes ?? [])];
  const nodes: Record<string, GraphNode> = {};
  for (const n of nodeList) nodes[n.item_id] = n;

  // `depends_on` only: `from` depends on `to`. Unknown ids and self edges are dropped.
  const deps = new Map<string, Set<string>>();
  const pairKeys = new Set<string>();
  const orderEdges: Array<[string, string]> = [];
  for (const e of (graph.edges ?? []) as GraphEdge[]) {
    if (e.kind !== "depends_on" || e.from === e.to || !(e.from in nodes) || !(e.to in nodes)) continue;
    const key = `${e.to}\u0000${e.from}`;
    if (pairKeys.has(key)) continue;
    pairKeys.add(key);
    orderEdges.push([e.to, e.from]);
    (deps.get(e.from) ?? deps.set(e.from, new Set()).get(e.from)!).add(e.to);
  }
  orderEdges.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0));

  // Level when the build supplied one, else the client-derived depth.
  // Server level where present; otherwise max(level of dependencies)+1 in dependency order, so a dependent
  // never sits left of its dependency even when only some nodes carry a level.
  const fixed = new Map<string, number>();
  for (const n of nodeList) if (typeof n.level === "number") fixed.set(n.item_id, n.level);
  const derived = deriveDepths(Object.keys(nodes), deps, fixed);
  const levelOf = (n: GraphNode) => derived.get(n.item_id) ?? 0;

  // Group into stations, then tracks (ordered by level value).
  const groups = new Map<string, { level: number; type: string; members: GraphNode[] }>();
  for (const n of nodeList) {
    const level = levelOf(n);
    const id = `L${level}|${n.metadata_type}`;
    (groups.get(id) ?? groups.set(id, { level, type: n.metadata_type, members: [] }).get(id)!).members.push(n);
  }
  const levels = [...new Set([...groups.values()].map((g) => g.level))].sort((a, b) => a - b);
  const trackIndex = new Map(levels.map((l, i) => [l, i]));

  const stations: Station[] = [];
  const stationOf: Record<string, string> = {};
  for (const [id, g] of groups) {
    g.members.sort(byName);
    const lampKeys = g.members.map(lampOf);
    const ringKeys = g.members.map(ringOf).filter((r): r is RingKind => r !== null);
    for (const m of g.members) stationOf[m.item_id] = id;
    stations.push({
      id,
      track: trackIndex.get(g.level)!,
      metadataType: g.type,
      itemIds: g.members.map((m) => m.item_id),
      count: g.members.length,
      lamps: tally(lampKeys, LAMP_ORDER).map(({ key, count }) => ({ lamp: key, count })),
      rings: tally(ringKeys, RING_ORDER).map(({ key, count }) => ({ ring: key, count })),
      settled: !lampKeys.some((l) => UNRESOLVED_LAMPS.has(l)),
    });
  }
  stations.sort((a, b) => a.track - b.track || (a.metadataType < b.metadataType ? -1 : a.metadataType > b.metadataType ? 1 : 0));

  const buildExists = nodeList.some((n) => n.build_status != null);
  const byTrack = new Map<number, Station[]>();
  for (const s of stations) (byTrack.get(s.track) ?? byTrack.set(s.track, []).get(s.track)!).push(s);
  const tracks: Track[] = levels.map((level, index) => {
    const mine = byTrack.get(index) ?? [];
    return {
      index,
      level,
      stationIds: mine.map((s) => s.id),
      itemCount: mine.reduce((sum, s) => sum + s.count, 0),
      settled: mine.every((s) => s.settled),
      active: false,
    };
  });
  const activeTrack = buildExists && opts.buildActive ? (tracks.find((t) => !t.settled)?.index ?? null) : null;
  if (activeTrack !== null) tracks[activeTrack].active = true;

  // Aggregate to station pairs (dependency station → dependent station).
  const agg = new Map<string, AggregatedEdge>();
  for (const [dep, dependent] of orderEdges) {
    const source = stationOf[dep];
    const target = stationOf[dependent];
    if (source === target) continue;
    const key = `${source}\u0000${target}`;
    const cur = agg.get(key);
    if (cur) cur.count += 1;
    else agg.set(key, { source, target, count: 1 });
  }
  const aggregatedEdges = [...agg.values()].sort((a, b) =>
    a.source < b.source ? -1 : a.source > b.source ? 1 : a.target < b.target ? -1 : a.target > b.target ? 1 : 0,
  );

  const lamps = tally(nodeList.map(lampOf), LAMP_ORDER).map(({ key, count }) => ({ lamp: key, count }));
  return { tracks, stations, lamps, aggregatedEdges, activeTrack, stationOf, nodes, orderEdges };
}

// ── layout ───────────────────────────────────────────────────────────────────────────────────────

export const LAYOUT = {
  colWidth: 210,
  stationWidth: 150,
  stationHeader: 54,
  itemHeight: 22,
  /** Rows of an expanded station drawn before its own scroll (edges attach to the first ones only). */
  maxVisibleRows: 40,
  gapY: 12,
  padX: 8,
  padY: 8,
  /** Most edge paths drawn; the rest are summarised (a 5000-node plan must not draw 5000 curves). */
  maxEdges: 300,
} as const;

export interface PlacedStation {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  expanded: boolean;
  /** ALL items when expanded, in order; the box is `maxVisibleRows` tall and scrolls. Edges attach to the first `maxVisibleRows` only. */
  rows: string[];
}
export interface PlacedEdge {
  key: string;
  /** Endpoint keys: a station id, or an item id when that item's station is expanded and the row is visible. */
  source: string;
  target: string;
  count: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Leads into the active track: carries the moving marker. */
  active: boolean;
}
export interface BoardLayout {
  width: number;
  height: number;
  stations: PlacedStation[];
  edges: PlacedEdge[];
  /** Edges not drawn because of `maxEdges`. */
  hiddenEdges: number;
}

export function layoutBoard(board: Board, expanded: ReadonlySet<string> = new Set()): BoardLayout {
  const L = LAYOUT;
  const placed = new Map<string, PlacedStation>();
  const stationById = new Map(board.stations.map((s) => [s.id, s]));
  const rowY = new Map<string, number>();
  let height = 0;
  for (const track of board.tracks) {
    let y = L.padY;
    for (const sid of track.stationIds) {
      const st = stationById.get(sid)!;
      const isOpen = expanded.has(sid);
      const rows = isOpen ? st.itemIds : [];
      const h = L.stationHeader + Math.min(rows.length, L.maxVisibleRows) * L.itemHeight;
      const x = L.padX + track.index * L.colWidth;
      placed.set(sid, { id: sid, x, y, w: L.stationWidth, h, expanded: isOpen, rows });
      rows.slice(0, L.maxVisibleRows).forEach((id, i) => rowY.set(id, y + L.stationHeader + i * L.itemHeight + L.itemHeight / 2));
      y += h + L.gapY;
    }
    height = Math.max(height, y);
  }
  const endpoint = (itemId: string): { key: string; cy: number; st: PlacedStation } => {
    const st = placed.get(board.stationOf[itemId])!;
    const row = rowY.get(itemId);
    return row !== undefined ? { key: itemId, cy: row, st } : { key: st.id, cy: st.y + L.stationHeader / 2, st };
  };

  const agg = new Map<string, PlacedEdge>();
  for (const [dep, dependent] of board.orderEdges) {
    if (board.stationOf[dep] === board.stationOf[dependent]) continue;
    const a = endpoint(dep);
    const b = endpoint(dependent);
    const key = `${a.key}\u0000${b.key}`;
    const cur = agg.get(key);
    if (cur) {
      cur.count += 1;
      continue;
    }
    const targetTrack = stationById.get(board.stationOf[dependent])!.track;
    agg.set(key, {
      key,
      source: a.key,
      target: b.key,
      count: 1,
      x1: a.st.x + a.st.w,
      y1: a.cy,
      x2: b.st.x,
      y2: b.cy,
      active: board.activeTrack !== null && targetTrack === board.activeTrack,
    });
  }
  const all = [...agg.values()].sort((p, q) => (p.key < q.key ? -1 : p.key > q.key ? 1 : 0));
  // Keep the heaviest edges when over the cap (ties by key) so the picture is stable between refetches.
  const edges =
    all.length <= L.maxEdges ? all : [...all].sort((p, q) => q.count - p.count || (p.key < q.key ? -1 : 1)).slice(0, L.maxEdges);
  return {
    width: L.padX * 2 + Math.max(1, board.tracks.length) * L.colWidth - (L.colWidth - L.stationWidth),
    height: Math.max(height, L.padY * 2),
    stations: board.stations.map((s) => placed.get(s.id)!),
    edges,
    hiddenEdges: all.length - edges.length,
  };
}
